import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { t } from "@lingui/core/macro";
import type { ReactNode } from "react";

export type SubscriptionLocationsValue = {
  allLocations: boolean;
  locationIds: number[];
};

export function SubscriptionLocationsField({
  value,
  locations,
  onChange,
  title,
  description,
  error,
  isLoading = false,
}: {
  value: SubscriptionLocationsValue;
  locations: { locationId: number; label: string }[];
  onChange: (value: SubscriptionLocationsValue) => void;
  title: string;
  description: string;
  error?: string;
  isLoading?: boolean;
}) {
  // A restricted value stays editable even when a single location is left.
  if (locations.length <= 1 && value.allLocations) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <LimeSwitch
        title={title}
        size={"sm"}
        isCompact={true}
        description={description}
        isSelected={value.allLocations}
        onValueChange={(isSelected) => {
          onChange({
            allLocations: isSelected,
            locationIds: isSelected ? [] : value.locationIds,
          });
        }}
        data-identifier="subscription-all-locations-switch"
      />
      <LimeCollapse isOpen={!value.allLocations}>
        <LimeSelect
          label={t`Lokacije`}
          selectionMode="multiple"
          disallowEmptySelection={false}
          showSelectAllToggle
          isLoading={isLoading}
          items={locations.map((location) => ({
            key: location.locationId.toString(),
            label: location.label,
          }))}
          value={value.locationIds.map(String)}
          onMultipleItemChange={(keys) => {
            onChange({ allLocations: false, locationIds: keys.map(Number) });
          }}
          error={error}
          data-identifier="subscription-locations-select"
        />
      </LimeCollapse>
    </div>
  );
}

type LocationScopeForm = {
  values: SubscriptionLocationsValue;
  errors: Record<string, ReactNode>;
  setValues: (values: SubscriptionLocationsValue) => void;
  clearFieldError: (path: "locationIds") => void;
};

export function CustomerSubscriptionLocationsField({
  form,
  locations,
  isLoading,
}: {
  form: LocationScopeForm;
  locations: { locationId: number; label: string }[];
  isLoading?: boolean;
}) {
  return (
    <SubscriptionLocationsField
      title={t`Uporaben na vseh lokacijah`}
      description={t`Izklopite, če lahko stranka paket uporablja samo na izbranih lokacijah.`}
      value={{
        allLocations: form.values.allLocations,
        locationIds: form.values.locationIds,
      }}
      locations={locations}
      isLoading={isLoading}
      error={form.errors.locationIds?.toString()}
      onChange={(value) => {
        form.setValues(value);
        form.clearFieldError("locationIds");
      }}
    />
  );
}
